import React, {useEffect} from 'react'
import PropTypes from 'prop-types'
import {
    Text,
    Stack,
    HStack,
    useBreakpointValue,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PDButton from '../../components/pd-button'
import usePdTagManager from '../../../../hooks/use-pd-tag-manager'

export const HeroPromotionBuyXForY = ({
    variant = 'hero',

    desktopPromotionBackground = 'transparent',
    mobilePromotionBackground = 'transparent',
    desktopPromotionPosition = 'middle-center',
    mobilePromotionPosition = 'middle',

    eyebrowText,
    eyebrowFontFamily = 'caslon',
    eyebrowColor = 'black',
    eyebrowTag = 'p',

    itemsCount,
    price,
    titleFontFamily = 'caslon',
    titleColor = 'black',
    titleTag = 'h2',

    subTitleText,
    subTitleFontFamily = 'caslon',
    subTitleColor = 'black',
    subTitleTag = 'h3',

    ctaTypeMobile = 'button',
    ctaTypeDesktop = 'button',
    buttonFontFamily = 'inter',

    buttonText1,
    buttonTextColor1,
    buttonBgColor1,
    buttonLink1,

    dropdownLinkLabel1,
    dropdownLinkUrl1,
    dropdownLinkLabel2,
    dropdownLinkUrl2,
    dropdownLinkLabel3,
    dropdownLinkUrl3,
    dropdownLinkLabel4,
    dropdownLinkUrl4,
    dropdownLinkLabel5,
    dropdownLinkUrl5,
    dropdownLinkLabel6,
    dropdownLinkUrl6,

    buttonText2,
    buttonLink2,
    buttonTextColor2,
    buttonBgColor2,

    typeId,
    name
}) => {
    const pdTagManager = usePdTagManager()
    useEffect(() => {
        pdTagManager.viewComponent(typeId, name)
    }, [])

    const styles = useMultiStyleConfig('HeroPromotions', {variant: `${variant}BuyXForY`})
    const hasButton1 = buttonText1 && buttonLink1
    const hasButton2 = buttonText2 && buttonLink2
    const ctaType1 = useBreakpointValue({base: ctaTypeMobile, md: ctaTypeDesktop})
    const ctaType2 = useBreakpointValue({base: ctaTypeMobile, md: ctaTypeDesktop})

    const dropdownLinks1 = [
        dropdownLinkLabel1 &&
            dropdownLinkUrl1 && {label: dropdownLinkLabel1, url: dropdownLinkUrl1},
        dropdownLinkLabel2 &&
            dropdownLinkUrl2 && {label: dropdownLinkLabel2, url: dropdownLinkUrl2},
        dropdownLinkLabel3 &&
            dropdownLinkUrl3 && {label: dropdownLinkLabel3, url: dropdownLinkUrl3},
        dropdownLinkLabel4 &&
            dropdownLinkUrl4 && {label: dropdownLinkLabel4, url: dropdownLinkUrl4},
        dropdownLinkLabel5 &&
            dropdownLinkUrl5 && {label: dropdownLinkLabel5, url: dropdownLinkUrl5},
        dropdownLinkLabel6 && dropdownLinkUrl6 && {label: dropdownLinkLabel6, url: dropdownLinkUrl6}
    ].filter(Boolean)

    return (
        <Stack
            gridArea={{base: mobilePromotionPosition, md: desktopPromotionPosition}}
            height="100%"
            justifyContent="center"
            position="relative"
        >
            <Stack
                backgroundColor={{base: mobilePromotionBackground, md: desktopPromotionBackground}}
                {...styles.wrapper}
                className="text-stack"
            >
                {eyebrowText && (
                    <Text
                        as={eyebrowTag}
                        className="eyebrow-text"
                        fontFamily={eyebrowFontFamily}
                        color={eyebrowColor}
                        {...styles.eyebrow}
                    >
                        {eyebrowText}
                    </Text>
                )}

                {itemsCount && price ? (
                    <Text
                        as={titleTag}
                        color={titleColor}
                        display="flex"
                        flexWrap="wrap"
                        justifyContent="center"
                        gap="10px"
                        className="title-text"
                        {...styles.title}
                    >
                        <Text as="span" fontFamily={titleFontFamily} className="items-count">
                            {itemsCount}
                        </Text>
                        <Text
                            as="span"
                            fontFamily={titleFontFamily}
                            {...styles.titleInner}
                            className="for-text"
                        >
                            For
                        </Text>
                        <Text as="span" fontFamily={titleFontFamily} className="dollar-sign">
                            $
                        </Text>
                        <Text as="span" fontFamily={titleFontFamily} className="text-price">
                            {price}
                        </Text>
                    </Text>
                ) : null}

                {subTitleText && (
                    <Text
                        as={subTitleTag}
                        fontFamily={subTitleFontFamily}
                        color={subTitleColor}
                        {...styles.subTitle}
                        className="sub-title-text"
                    >
                        {subTitleText}
                    </Text>
                )}

                {hasButton1 || hasButton2 ? (
                    <HStack
                        gap={{base: '20px', md: '20px'}}
                        justifyContent="center"
                        flexWrap="wrap"
                        marginTop={{base: '20px', md: '25px'}}
                        className="promo-buttons-wrapper"
                    >
                        {hasButton1 && (
                            <PDButton
                                text={buttonText1}
                                url={buttonLink1}
                                color={buttonTextColor1}
                                ctaType={ctaType1}
                                buttonBackgroundColor={buttonBgColor1}
                                buttonFontFamily={buttonFontFamily}
                                links={dropdownLinks1}
                                typeId={typeId}
                                name={name}
                            />
                        )}

                        {hasButton2 && (
                            <PDButton
                                text={buttonText2}
                                url={buttonLink2}
                                color={buttonTextColor2}
                                ctaType={ctaType2}
                                buttonBackgroundColor={buttonBgColor2}
                                buttonFontFamily={buttonFontFamily}
                                typeId={typeId}
                                name={name}
                            />
                        )}
                    </HStack>
                ) : null}
            </Stack>
        </Stack>
    )
}

HeroPromotionBuyXForY.propTypes = {
    variant: PropTypes.string,
    desktopPromotionBackground: PropTypes.string,
    mobilePromotionBackground: PropTypes.string,
    desktopPromotionPosition: PropTypes.string,
    mobilePromotionPosition: PropTypes.string,

    eyebrowText: PropTypes.string,
    eyebrowFontFamily: PropTypes.string,
    eyebrowColor: PropTypes.string,
    eyebrowTag: PropTypes.string,

    itemsCount: PropTypes.string,
    price: PropTypes.string,
    titleFontFamily: PropTypes.string,
    titleColor: PropTypes.string,
    titleTag: PropTypes.string,

    subTitleText: PropTypes.string,
    subTitleFontFamily: PropTypes.string,
    subTitleColor: PropTypes.string,
    subTitleTag: PropTypes.string,

    ctaTypeMobile: PropTypes.string,
    ctaTypeDesktop: PropTypes.string,
    ctaSizeMobile: PropTypes.string,
    ctaSizeDesktop: PropTypes.string,

    buttonText1: PropTypes.string,
    buttonColor1: PropTypes.string,
    buttonTextColor1: PropTypes.string,
    buttonBgColor1: PropTypes.string,
    buttonTextColor2: PropTypes.string,
    buttonBgColor2: PropTypes.string,
    buttonLink1: PropTypes.string,
    buttonFontFamily: PropTypes.string,
    dropdownLinkLabel1: PropTypes.string,
    dropdownLinkUrl1: PropTypes.string,
    dropdownLinkLabel2: PropTypes.string,
    dropdownLinkUrl2: PropTypes.string,
    dropdownLinkLabel3: PropTypes.string,
    dropdownLinkUrl3: PropTypes.string,
    dropdownLinkLabel4: PropTypes.string,
    dropdownLinkUrl4: PropTypes.string,
    dropdownLinkLabel5: PropTypes.string,
    dropdownLinkUrl5: PropTypes.string,
    dropdownLinkLabel6: PropTypes.string,
    dropdownLinkUrl6: PropTypes.string,

    buttonText2: PropTypes.string,
    buttonLink2: PropTypes.string,

    typeId: PropTypes.string,
    name: PropTypes.any
}

export default HeroPromotionBuyXForY
